"use client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { getCommentsAction } from "@/actions/content.action";
import type { PaginatedResult } from "@/lib/fetch";
import type { Comment } from "@/types/content";
import CommentCard from "./comment-card";
import CommentInput from "./comment-input";

const COMMENT_ESTIMATED_HEIGHT = 160;

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  contentId: string;
  initialData?: PaginatedResult<Comment>;
  isLoggedIn: boolean;
  user?: CurrentUser;
}

const CommentsClient = ({
  contentId,
  initialData,
  isLoggedIn,
  user,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      getNextPageParam: (last) =>
        last && last.page < last.totalPages ? last.page + 1 : undefined,
      initialData: initialData
        ? { pageParams: [1], pages: [initialData] }
        : undefined,
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        getCommentsAction(contentId, pageParam as number),
      queryKey: ["comments", contentId],
      staleTime: 30_000,
    });

  const allComments = data?.pages.flatMap((p) => p?.data ?? []) ?? [];

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allComments.length + 1 : allComments.length,
    estimateSize: () => COMMENT_ESTIMATED_HEIGHT,
    overscan: 5,
    scrollMargin: containerRef.current?.offsetTop ?? 0,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }
    if (
      lastItem.index >= allComments.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    allComments.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return (
    <div>
      <CommentInput contentId={contentId} isLoggedIn={isLoggedIn} user={user} />
      <div
        ref={containerRef}
        style={{ height: virtualizer.getTotalSize(), position: "relative" }}
      >
        {virtualItems.map((item) => {
          const isLoader = item.index === allComments.length;
          return (
            <div
              data-index={item.index}
              key={item.key}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)`,
                width: "100%",
              }}
            >
              {isLoader ? (
                <div className="py-4 text-center text-sm text-neutral-400">
                  Yükleniyor...
                </div>
              ) : (
                <CommentCard
                  comment={allComments[item.index]}
                  contentId={contentId}
                  currentUser={user}
                  isLoggedIn={isLoggedIn}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommentsClient;
